/**
 * The Meta pixel.
 *
 * Loaded lazily, and ONLY after `lib/consent` reports "granted". There is
 * deliberately no snippet in index.html: a pixel in the head runs before any
 * consent decision exists, which is the exact thing the banner is for.
 *
 * Gated on VITE_META_PIXEL_ID, so an unset variable makes every function here a
 * no-op rather than an error. Note VITE_* values are baked in at BUILD time —
 * changing the id on Render requires "Clear build cache & deploy", not a
 * restart.
 */

const PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID as string | undefined) ?? '';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
};

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

/** Custom events. Standard ones are passed as plain strings at the call site. */
export const META_EVENTS = {
  /** The reader left the hero and entered the form. Standard event. */
  startedForm: 'ViewContent',
  /**
   * Step 1 completed: the apartment has an address and a surface. CUSTOM, and
   * the campaign's optimisation target — it means the owner identified a real
   * apartment and chose to carry on, a materially better predictor than an
   * abandoned first field.
   */
  apartmentLocated: 'ApartmentLocated',
  /** The submission was accepted by the server. Standard event. */
  lead: 'Lead',
} as const;

let loaded = false;

export function isMetaConfigured(): boolean {
  return PIXEL_ID !== '';
}

/**
 * Injects fbevents.js and fires the initial PageView. Idempotent, so calling it
 * on every consent change or re-render is harmless.
 */
export function loadMetaPixel(): void {
  if (loaded || !PIXEL_ID || typeof window === 'undefined') return;
  loaded = true;

  /* Meta's bootstrap stub, transcribed rather than pasted: it defines window.fbq
     so calls made before the script finishes are queued by Meta's own code. */
  if (!window.fbq) {
    const stub: Fbq = function (...args: unknown[]) {
      if (stub.callMethod) stub.callMethod(...args);
      else (stub.queue as unknown[]).push(args);
    } as Fbq;
    stub.queue = [];
    stub.loaded = true;
    stub.version = '2.0';
    window.fbq = stub;
    if (!window._fbq) window._fbq = stub;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  /*
   * Meta's automatic configuration is left ON, deliberately.
   *
   * It means the pixel reports more than the four events below: it logs page
   * interactions by itself (a test run produced five `SubscribedButtonClick`
   * entries), and where Automatic Advanced Matching is enabled on the dataset it
   * also hashes contact details out of the form to match visits to Meta
   * accounts. Both improve match quality, which is why they stay on.
   *
   * The cost is paid in disclosure, not in silence: `privacy.sections` names
   * the automatic logging and the hashed contact details explicitly. If that
   * paragraph is ever trimmed back, this line has to be reconsidered — the
   * policy and the pixel are one decision, and this is the half that is easy to
   * forget.
   */
  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

/**
 * Fires an event, or does nothing.
 *
 * Events raised before consent are DROPPED, never queued for later replay.
 * Queuing would mean that accepting the banner retroactively reports behaviour
 * from when the reader had not yet agreed, which is not what they agreed to.
 * The cost is losing the mid-funnel events of anyone who works through the form
 * before deciding; that is the correct trade.
 */
export function trackMeta(event: string, isCustom = false): void {
  if (!loaded || !window.fbq) return;
  window.fbq(isCustom ? 'trackCustom' : 'track', event);
}
